/* eslint-disable no-underscore-dangle */
import axios, { AxiosRequestConfig, AxiosResponse, ResponseType } from 'axios';
import {
  acquireAccessToken,
  retrieveAccessToken,
  storeAccessToken,
} from './security';

const axiosApiInstance = axios.create();

export interface IResult<T> {
  content: T | null;
  message: string;
  status: number;
}

export function getApi<T>(
  endpoint: string,
  responseType?: ResponseType | undefined
): Promise<AxiosResponse<IResult<T>, unknown>> {
  return axiosApiInstance.get<IResult<T>, any>(
    `${process.env.REACT_APP_API_URL}${endpoint}`,
    {
      responseType,
    }
  );
}

export function getApiCached<T>(
  endpoint: string
): Promise<AxiosResponse<IResult<T>, unknown>> {
  const sessionStorageData = sessionStorage.getItem(endpoint);

  if (sessionStorageData) {
    const cachedData = JSON.parse(sessionStorageData) as T;
    return new Promise<AxiosResponse<IResult<T>, unknown>>((resolve) => {
      const fakeResult = {
        content: cachedData,
        message: '',
        status: 200,
      } as IResult<T>;
      const fakeResponse: AxiosResponse<IResult<T>, unknown> = {
        data: fakeResult,
        status: 200,
        statusText: '',
        config: {},
        headers: {},
      };
      resolve(fakeResponse);
    });
  }

  return new Promise<AxiosResponse<IResult<T>, unknown>>((resolve, reject) => {
    const call = axiosApiInstance.get<IResult<T>, any>(
      `${process.env.REACT_APP_API_URL}${endpoint}`
    );

    call
      .then((response) => {
        sessionStorage.setItem(endpoint, JSON.stringify(response.data.content));
        resolve(call);
      })
      .catch((e) => reject(e));
  });
}

export { axiosApiInstance };

export function postApi<T, V>(
  endpoint: string,
  payload: V | null
): Promise<AxiosResponse<IResult<T>, unknown>> {
  console.log(payload);
  return axiosApiInstance.post<IResult<T>>(
    `${process.env.REACT_APP_API_URL}${endpoint}`,
    payload
  );
}
export function putApi<T, V>(
  endpoint: string,
  payload: V | null
): Promise<AxiosResponse<IResult<T>, unknown>> {
  console.log(payload);
  return axiosApiInstance.put<IResult<T>>(
    `${process.env.REACT_APP_API_URL}${endpoint}`,
    payload
  );
}

export function deleteApi<T>(
  endpoint: string
): Promise<AxiosResponse<IResult<T>, unknown>> {
  return axiosApiInstance.delete<IResult<T>>(
    `${process.env.REACT_APP_API_URL}${endpoint}`
  );
}

export function mapResult<T>(response: AxiosResponse<IResult<T>, unknown>): T {
  if (response.status !== 200) {
    throw new Error(
      `Received status code ${response.status} when calling ${response.request?.responseURL}`
    );
  }

  if (response.data.content === null) {
    throw new Error('Content was unexpectedly null');
  }

  return response.data.content;
}

// Refresh this long before expiry so a request never leaves with a token
// that dies in transit (also absorbs modest client/server clock skew).
const TOKEN_EXPIRY_BUFFER_MS = 2 * 60 * 1000;

function tokenExpiresSoon(token: string): boolean {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return exp * 1000 - Date.now() < TOKEN_EXPIRY_BUFFER_MS;
  } catch {
    return true; // unreadable token — treat as stale and refresh
  }
}

axiosApiInstance.interceptors.request.use(
  async (c: AxiosRequestConfig<unknown>) => {
    const config = c;
    let accessToken = retrieveAccessToken();

    // Proactively renew an expired/near-expiry token instead of letting the
    // request bounce off the server with a 401 (which pollutes server logs
    // and costs the user a round-trip). The 401 response interceptor below
    // stays as a safety net for anything this check misses.
    if (accessToken && tokenExpiresSoon(accessToken)) {
      try {
        accessToken = await acquireAccessToken(true);
        storeAccessToken(accessToken);
      } catch {
        // Refresh failed — send the request anyway and let the 401
        // interceptor make the final attempt.
      }
    }

    if (accessToken) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${accessToken}`,
        'Access-Control-Allow-Origin': process.env.REACT_APP_ORIGIN_URL ?? '',
      };
      config.withCredentials = true;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosApiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      // The server just rejected the token we hold, so a cached MSAL result
      // is no good — force a network refresh.
      const token = await acquireAccessToken(true);
      storeAccessToken(token);
      return axiosApiInstance(originalRequest);
    }
    return Promise.reject(error);
  }
);
